/**
 * Avatar — initials fallback (no image source) or solid image.
 * BrandAvatar — square G-mark logo bubble for the assistant avatar slot.
 */

import React from 'react';

interface AvatarProps {
  name?: string;
  email?: string;
  src?: string;
  size?: number;
  square?: boolean;
  style?: React.CSSProperties;
}

const initialsFor = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  if (email) {
    const local = email.split('@')[0]!;
    const dot = local.split(/[._-]/).filter(Boolean);
    if (dot.length >= 2) return (dot[0]![0]! + dot[1]![0]!).toUpperCase();
    return local.slice(0, 2).toUpperCase();
  }
  return '··';
};

export const Avatar: React.FC<AvatarProps> = ({
  name,
  email,
  src,
  size = 32,
  square,
  style,
}) => {
  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: square ? 'var(--radius-md)' : '50%',
    background: src
      ? `url(${src}) center/cover, var(--cg-gradient-hero)`
      : 'var(--cg-gradient-hero)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: Math.round(size * 0.36),
    fontWeight: 700,
    flexShrink: 0,
    fontFamily: 'var(--font-sans)',
    letterSpacing: '0.02em',
    overflow: 'hidden',
    ...style,
  };
  return (
    <div style={base} aria-label={name || email}>
      {!src && initialsFor(name, email)}
    </div>
  );
};

interface BrandAvatarProps {
  size?: number;
  thinking?: boolean;
  style?: React.CSSProperties;
}

export const BrandAvatar: React.FC<BrandAvatarProps> = ({ size = 32, thinking, style }) => {
  const base: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 'var(--radius-md)',
    background: '#fff',
    border: '1px solid var(--border-1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'var(--shadow-xs)',
    position: 'relative',
    ...style,
  };
  return (
    <div style={base}>
      {thinking && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: -2,
            borderRadius: 'var(--radius-md)',
            background: 'var(--cg-gradient-pulse)',
            opacity: 0.5,
            animation: 'cgConicSpin 3.2s linear infinite',
            filter: 'blur(2px)',
            zIndex: 0,
          }}
        />
      )}
      <img
        src="/assets/logo.svg"
        alt=""
        width={Math.round(size * 0.62)}
        height={Math.round(size * 0.62)}
        style={{ display: 'block', position: 'relative', zIndex: 1, borderRadius: 0, maxWidth: 'unset', maxHeight: 'unset' }}
      />
    </div>
  );
};

export default Avatar;
